import { useMemo } from "react";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import { useViewportWidth } from "@/hooks/use-viewport-width";
import { COLUMN_REGISTRY } from "../config/columns";
import {
  buildCustomColumns,
  buildInlineCustomFieldColumns,
  filterDefsByMode,
  isCustomColumnList,
  withCustomColumns,
  QUANTITY_ANCHOR_IDS,
  sumColumnWidths,
  TABLE_CHROME_ALLOWANCE_PX,
} from "../config/customColumns";
import type { ColumnDef, RecapModeConfig } from "../config/types";
import type { ViewMode } from "../types";
import type { CustomFieldDefinition } from "@/app/api/types/featureFlags";

/**
 * Resolves visible columns based on mode, viewMode, and feature flags.
 * This is the single place that checks feature flags for column visibility —
 * no scattered flag checks in components.
 */
export function useRecapColumns(
  mode: RecapModeConfig,
  viewMode: ViewMode,
  /**
   * Measured width of the element that actually contains the table. Pass it
   * whenever the caller can measure — the viewport is only a fallback, and a
   * poor one: the chat panel, a resized sidebar and any side/detail panel all
   * shrink the table without changing `window.innerWidth`.
   */
  containerWidth?: number,
): ColumnDef[] {
  const showUnitRecap = useFeatureFlag("showUnitRecap");
  const showWeightRecap = useFeatureFlag("showWeightRecap");
  const showPricesRecap = useFeatureFlag("showPricesRecap");
  const hidePreOrderNumber = useFeatureFlag("hidePreOrderNumber");
  const showProductImages = useFeatureFlag("showProductImages");
  // Array-shaped flag: column visibility depends on whether the company has
  // configured any custom fields, not a plain boolean.
  const flowCustomFields = useFeatureFlag("preOrderFlowCustomFields") as
    | CustomFieldDefinition[]
    | undefined;
  // Flag-defined custom value columns (Rolls / Pallets …) — appended after the
  // static registry so each renders as its own inline numeric column.
  const flowCustomColumns = useFeatureFlag("preOrderFlowCustomColumns");

  // Whether custom fields render as their own columns or collapse into the
  // popover is a question of FIT, not of breakpoint: it depends on how many
  // fields the company configured AND on how much room the table actually has.
  // Prefer the measured container; fall back to the viewport (minus page
  // chrome) only when the caller cannot measure.
  const viewportWidth = useViewportWidth();
  const availableWidth =
    containerWidth && containerWidth > 0
      ? containerWidth
      : viewportWidth > 0
        ? viewportWidth - TABLE_CHROME_ALLOWANCE_PX
        : 0;

  return useMemo(() => {
    // Per-def mode scoping: a def may declare `modes: ["purchases"]` to only
    // exist on that surface; unscoped defs apply to both (legacy behavior).
    const customColumns = buildCustomColumns(
      isCustomColumnList(flowCustomColumns)
        ? filterDefsByMode(flowCustomColumns, mode)
        : [],
    );
    // Inline custom field columns replace the old `custom_fields` popover
    // column — each field gets its own column for better visibility.
    const scopedFieldDefs = filterDefsByMode(flowCustomFields ?? [], mode);
    const hasFlowCustomFields = scopedFieldDefs.length > 0;
    const inlineFieldColumns = buildInlineCustomFieldColumns(scopedFieldDefs);
    // Measure what the table would actually need, then decide. `staticColumns`
    // isn't resolved yet at this point, so the static side is measured below
    // and the decision is deferred until both halves are known.
    const staticColumns = COLUMN_REGISTRY.filter((col) => {
      // Mode filter
      if (!col.modes.includes(mode)) return false;

      // ViewMode filter
      if (col.viewModes && !col.viewModes.includes(viewMode)) return false;

      // Feature flag filter
      if (col.featureFlag) {
        const flagValue = getFlagValue(col.featureFlag, {
          showUnitRecap,
          showWeightRecap,
          showPricesRecap,
          hidePreOrderNumber,
          showProductImages,
        });
        // Normal: shown unless flag explicitly set to false
        // Inverse: shown unless flag explicitly set to true
        const visible = col.featureFlagInverse
          ? flagValue !== true
          : flagValue !== false;
        if (!visible) return false;
      }

      return true;
    });

    // Does everything fit? Sum the real widths of every column that would be
    // rendered inline and compare against the space the page actually has.
    // viewportWidth === 0 means "not measured yet" (SSR / first paint) — treat
    // that as "doesn't fit" so we start compact and never flash a clipped table.
    // Going inline REPLACES the single popover column with N field columns, so
    // exclude the popover from the measurement or we'd charge ourselves for a
    // column that wouldn't be rendered.
    const inlineWidth =
      sumColumnWidths(staticColumns.filter((c) => c.id !== "custom_fields")) +
      sumColumnWidths(customColumns) +
      sumColumnWidths(inlineFieldColumns);
    // availableWidth === 0 means "not measured yet" (SSR / first paint) — treat
    // that as "doesn't fit" so we start compact and never flash a clipped table.
    const fits = availableWidth > 0 && availableWidth >= inlineWidth;
    const useInline = fits && inlineFieldColumns.length > 0;

    // The popover column exists only when the fields did NOT go inline.
    const resolvedStatic = staticColumns.filter((col) =>
      col.id === "custom_fields" ? !useInline && hasFlowCustomFields : true,
    );

    // Two different placements, because the two kinds mean different things:
    //  * VALUE columns (Palettes, Rolls…) are quantities — they belong with
    //    quantity/unit/weight, so anchor them before `price`. This matches
    //    what transport mode already does.
    //  * FIELD columns (GGN, dates, poste…) are attributes — they stay in the
    //    data region just before the trailing control columns.
    const withValueColumns = withCustomColumns(
      resolvedStatic,
      customColumns,
      QUANTITY_ANCHOR_IDS,
    );
    return useInline
      ? withCustomColumns(withValueColumns, inlineFieldColumns)
      : withValueColumns;
  }, [
    mode,
    viewMode,
    showUnitRecap,
    showWeightRecap,
    showPricesRecap,
    hidePreOrderNumber,
    showProductImages,
    availableWidth,
    flowCustomFields,
    flowCustomColumns,
  ]);
}

/**
 * Resolves columns for standalone use (chat pre-order divider, etc.).
 * Same feature-flag logic as useRecapColumns, but fixed to sales + per-client,
 * excludes total/partner/order_number.
 */
export function useStandaloneColumns(): ColumnDef[] {
  const showUnitRecap = useFeatureFlag("showUnitRecap");
  const showWeightRecap = useFeatureFlag("showWeightRecap");
  const showPricesRecap = useFeatureFlag("showPricesRecap");
  const showProductImages = useFeatureFlag("showProductImages");

  return useMemo(() => {
    const EXCLUDED: Set<string> = new Set([
      "total",
      "partner",
      "order_number",
      "select",
    ]);

    return COLUMN_REGISTRY.filter((col) => {
      if (EXCLUDED.has(col.id)) return false;
      if (!col.modes.includes("sales")) return false;
      if (col.viewModes) return false; // skip per-product-only columns

      if (col.featureFlag) {
        const flagValue = getFlagValue(col.featureFlag, {
          showUnitRecap,
          showWeightRecap,
          showPricesRecap,
          showProductImages,
        });
        const visible = col.featureFlagInverse
          ? flagValue !== true
          : flagValue !== false;
        if (!visible) return false;
      }

      return true;
    });
  }, [showUnitRecap, showWeightRecap, showPricesRecap, showProductImages]);
}

function getFlagValue(
  flagName: string,
  flags: Record<string, unknown>,
): unknown {
  return flags[flagName];
}
